import { Subject, Student, PublishedResult, SchoolClass, Section, CurriculumType } from '../types';
import { getPublishedResultsForStudent } from './resultMatching';

// Subjects that trigger a mandatory resit + promotion block when failed.
// Matched case-insensitively against Subject.name.
export const RESIT_SUBJECTS = ['Cambridge English', 'Cambridge Mathematics', 'Cambridge Science'];

/**
 * Normalizes subject names to standardise spacing, handle spelling typos, and resolve abbreviations.
 */
export const normalizeSubjectName = (name: string): string => {
    return (name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/\bmaths\b/g, 'mathematics')
        .replace(/\bmath\b/g, 'mathematics')
        .replace(/\bcambrigde\b/g, 'cambridge');
};

export const isResitSubject = (subjectName: string): boolean => {
    const normalized = normalizeSubjectName(subjectName);
    return RESIT_SUBJECTS.map(s => normalizeSubjectName(s)).includes(normalized);
};

export const computeGrade = (total: number): { grade: string; remark: string } => {
    if (total >= 75) return { grade: 'A1', remark: 'Excellent' };
    if (total >= 70) return { grade: 'B2', remark: 'Very Good' };
    if (total >= 65) return { grade: 'B3', remark: 'Good' };
    if (total >= 60) return { grade: 'C4', remark: 'Credit' };
    if (total >= 55) return { grade: 'C5', remark: 'Credit' };
    if (total >= 50) return { grade: 'C6', remark: 'Credit' };
    if (total >= 45) return { grade: 'D7', remark: 'Pass' };
    if (total >= 40) return { grade: 'E8', remark: 'Pass' };
    return { grade: 'F9', remark: 'Fail' };
};

// Mirrors the defensive term-coercion already used in App.tsx's grade computation —
// `term` isn't always a clean number for older records, so treat anything missing/
// non-numeric as Term 1 rather than accidentally matching Term 3.
const resolveTerm = (term: number | string | undefined): number =>
    typeof term === 'number' ? term : Number(term || 1);

// A subject needs a resit if it's currently failing (F9) in one of the gated subjects
// for TERM 3 SPECIFICALLY — promotion is decided on the final term's result, so a past
// F9 in Term 1 or 2 that was since passed must never linger as a resit requirement.
// Deliberately not persisted — grade is always derived from ca1+ca2+exam elsewhere in
// this codebase, so this stays derived too rather than risking going stale.
export const needsResit = (subject: Pick<Subject, 'name' | 'ca1' | 'ca2' | 'exam' | 'resitStatus' | 'term'>): boolean => {
    if (resolveTerm(subject.term) !== 3) return false;
    if (!isResitSubject(subject.name)) return false;
    if (subject.resitStatus === 'passed') return false;
    const total = (subject.ca1 || 0) + (subject.ca2 || 0) + (subject.exam || 0);
    return total < 50;
};

export const hasBlockingResit = (subjects: Subject[] | undefined): boolean =>
    (subjects || []).some(needsResit);

// End-of-year repeat/promotion policy: failing (unresolved resit) in 2 or all 3 of the
// gated Cambridge subjects (English/Mathematics/Science) means the student repeats the
// class outright; failing exactly 1 promotes them CONDITIONALLY rather than blocking
// promotion entirely; failing none promotes them clear.
export type PromotionStatus = 'clear' | 'conditional' | 'repeat';

export const countBlockingResits = (subjects: Subject[] | undefined): number =>
    (subjects || []).filter(needsResit).length;

// The gated subjects (RESIT_SUBJECTS) are exclusively Cambridge/conventional-track
// subjects — repeating or conditionally-promoting a student because of them makes no
// sense for an Islamiyya, Tahfiz, or Early Years class, which never teaches Cambridge
// English/Mathematics/Science. When the class's curriculumType is known and isn't
// 'conventional', this policy simply doesn't apply, regardless of what a student's
// (possibly dual-enrolled, or mislabeled legacy) subject records contain.
export const getPromotionStatus = (subjects: Subject[] | undefined, curriculumType?: CurriculumType | null): PromotionStatus => {
    if (curriculumType && curriculumType !== 'conventional') return 'clear';
    const count = countBlockingResits(subjects);
    if (count >= 2) return 'repeat';
    if (count === 1) return 'conditional';
    return 'clear';
};

// Prefer the STUDENT'S OFFICIAL PUBLISHED report card for the term when one exists (the
// approved, immutable record already shown to the student/parent). Matching by `studentId`
// alone misses most historical records (many published_results docs predate that field, or
// belong to a student re-enrolled under a different doc), so this uses the same robust,
// multi-strategy matcher (`matchesPublishedResult`) the StudentPortal relies on. Falls back to
// the live gradebook (`student.subjects`) only if genuinely no published match exists — e.g.
// the term's review hasn't been approved yet.
// `classId`, when passed, scopes the result down to just that one class — required
// whenever the caller is about to make a per-class decision (promotion/repeat, resit
// gating, PDF notices) on a student who may be dual-enrolled (e.g. a conventional class
// plus Islamiyya/Tahfiz): without this, a subject from a completely different class
// (including one literally named "Cambridge English") leaks into decisions that have
// nothing to do with it. Omit `classId` for whole-student views (e.g. overall scholarship
// risk) that intentionally look across every class the student is in.
export const getPublishedSubjectsForTerm = (
    student: Student,
    publishedResults: PublishedResult[] | undefined,
    classes: SchoolClass[] | undefined,
    sections: Section[] | undefined,
    term: number = 3,
    classId?: string
): Subject[] => {
    const matches = getPublishedResultsForStudent(publishedResults, student, classes, sections)
        .filter(r => Number(r.term) === term);

    const subjectMap = new Map<string, Subject>();

    const mergeSubject = (s: Subject, cid: string) => {
        const normName = normalizeSubjectName(s.name);
        const key = `${cid}_${normName}`;
        const existing = subjectMap.get(key);
        const sTerm = typeof s.term === 'number' ? s.term : Number(s.term || term);

        const sTotal = (s.ca1 || 0) + (s.ca2 || 0) + (s.exam || 0);
        const sPassed = s.resitStatus === 'passed' || sTotal >= 50;

        if (!existing) {
            subjectMap.set(key, {
                ...s,
                term: sTerm,
                classId: cid,
                resitStatus: (sTotal >= 50 && s.originalExamBeforeResit !== undefined) ? 'passed' : s.resitStatus
            });
            return;
        }

        const existingTotal = (existing.ca1 || 0) + (existing.ca2 || 0) + (existing.exam || 0);
        const existingPassed = existing.resitStatus === 'passed' || existingTotal >= 50;

        if (sPassed && !existingPassed) {
            subjectMap.set(key, {
                ...existing,
                ...s,
                resitStatus: s.resitStatus || ((sTotal >= 50 && (s.originalExamBeforeResit !== undefined || existing.originalExamBeforeResit !== undefined)) ? 'passed' : existing.resitStatus),
                term: sTerm,
                classId: cid
            });
        } else if (!sPassed && existingPassed) {
            subjectMap.set(key, {
                ...existing,
                resitScheduledAt: s.resitScheduledAt ?? existing.resitScheduledAt,
                resitRecordedAt: s.resitRecordedAt ?? existing.resitRecordedAt,
                resitScore: s.resitScore ?? existing.resitScore,
                originalExamBeforeResit: s.originalExamBeforeResit ?? existing.originalExamBeforeResit,
            });
        } else {
            const winner = sTotal >= existingTotal ? s : existing;
            subjectMap.set(key, {
                ...existing,
                ...s,
                ...winner,
                resitStatus: (sPassed || existingPassed) ? (s.resitStatus === 'passed' || existing.resitStatus === 'passed' ? 'passed' : winner.resitStatus) : (s.resitStatus || existing.resitStatus),
                resitScheduledAt: s.resitScheduledAt ?? existing.resitScheduledAt,
                resitRecordedAt: s.resitRecordedAt ?? existing.resitRecordedAt,
                resitScore: s.resitScore ?? existing.resitScore,
                originalExamBeforeResit: s.originalExamBeforeResit ?? existing.originalExamBeforeResit,
                term: sTerm,
                classId: cid
            });
        }
    };

    if (matches.length > 0) {
        const latestByClass = new Map<string, PublishedResult>();
        matches.forEach(r => {
            const cid = r.classId || '';
            const existing = latestByClass.get(cid);
            if (!existing || (r.approvedAt || 0) > (existing.approvedAt || 0)) {
                latestByClass.set(cid, r);
            }
        });

        latestByClass.forEach((r, cid) => {
            if (r.subjects) {
                r.subjects.forEach(s => mergeSubject(s, cid));
            }
        });

        matches.forEach(r => {
            const cid = r.classId || '';
            if (r.subjects) {
                r.subjects.forEach(s => mergeSubject(s, cid));
            }
        });
    }

    if (student.subjects && student.subjects.length > 0) {
        student.subjects.forEach(s => {
            const sTerm = typeof s.term === 'number' ? s.term : Number(s.term || 1);
            if (sTerm === term) {
                const sClassId = s.classId || classId || '';
                mergeSubject(s, sClassId);
            }
        });
    }

    let subjects = Array.from(subjectMap.values());

    if (subjects.length === 0 && student.subjects) {
        subjects = (student.subjects || []).map(s => ({ ...s, term: s.term ?? term }));
    }

    if (!classId) return subjects;
    const className = classes?.find(c => c.id === classId)?.name || '';
    return filterSubjectsForClassTerm(subjects, term, classId, className);
};

export const scholarshipThreshold = (type: NonNullable<Student['scholarship']>['type']): number =>
    type === 'Merit' ? 95 : 85;

// A student's `subjects` array can span multiple classes/sections they're enrolled in (e.g. a
// conventional class plus Islamiyya/Tahfiz) — anywhere that reports on "this one class/term"
// (archive previews, downloads, republishing) must scope down to just that class, or subjects
// from an unrelated section inflate the subject count and skew the average/grade. Mirrors the
// classId + legacy-Islamiyya filter already used when a review is first approved.
export const filterSubjectsForClassTerm = (
    subjects: Subject[] | undefined,
    term: number,
    classId: string | undefined,
    className: string
): Subject[] => {
    const isIslamiyya = (className || '').toLowerCase().includes('islamiyya') || (className || '').toLowerCase().includes('tahfiz');
    return (subjects || []).filter(s => {
        const sTerm = typeof s.term === 'number' ? s.term : Number(s.term || 1);
        if (sTerm !== Number(term)) return false;

        // Legacy Islamiyya/Tahfiz subjects were sometimes stored without a classId at
        // all — the Arabic-name heuristic is the only way to recognise them as this
        // class's own. This is a deliberate, narrow exception; every other classId-less
        // subject is treated as unscoped/unknown and excluded below rather than assumed
        // to belong here (that assumption is exactly what let another class's subjects,
        // e.g. a genuine "Cambridge English", silently leak into unrelated decisions).
        if (isIslamiyya && !s.classId) {
            return /[؀-ۿ]/.test(s.name);
        }

        if (!classId) return true;
        return s.classId === classId;
    });
};
